import type { FC, MouseEvent } from "react";
import { useCallback } from "react";
import type { Doc } from "sharedb/lib/client";
import { twMerge } from "tailwind-merge";

import type { BlockType, FieldType, WorksheetType } from "../../shared/types/worksheet";
import { STATIC_CONTENT_FIELD_TYPES, isQuestionBlock } from "../../shared/worksheets/constants";

import { showTemplateBlockModal } from "./TemplateBlockModal";

import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { PageIcon } from "mds/icons";
import type { DocViewContextType } from "providers/DocViewProvider";

// eslint-disable-next-line react-refresh/only-export-components
export function getTemplateFieldIndex({
  accessId,
  block,
  canViewTemplateModal,
  showResponses,
}: {
  accessId: string;
  block: BlockType | undefined;
  canViewTemplateModal: boolean;
  showResponses: DocViewContextType["showResponses"];
}): number {
  if (!block || !accessId || !canViewTemplateModal || showResponses) {
    return -1;
  }

  // Never show this affordance for static-only blocks.
  if (!isQuestionBlock(block)) {
    return -1;
  }

  const firstNonStaticFieldIndex = (block.f as FieldType[]).findIndex(
    (f) => f && !STATIC_CONTENT_FIELD_TYPES.includes(f.t),
  );

  return firstNonStaticFieldIndex !== -1 ? firstNonStaticFieldIndex : 0;
}

type TemplateBlockButtonProps = {
  block: BlockType;
  doc: Doc<WorksheetType>;
  docViewContextValue: DocViewContextType;
  versionType: "instructor" | "template";
  className?: string;
  hidden?: boolean;
};

export const TemplateBlockButton: FC<TemplateBlockButtonProps> = ({
  block,
  doc,
  docViewContextValue,
  className,
  hidden,
  versionType,
}) => {
  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      showTemplateBlockModal({ block, doc, docViewContextValue, versionType });
    },
    [block, doc, docViewContextValue, versionType],
  );

  return (
    <Tooltip
      className={twMerge(hidden && "hidden")}
      content={t(`worksheet.template_block_modal.${versionType}.title`)}
    >
      <SquareButton
        aria-label={t(`worksheet.template_block_modal.${versionType}.title`)}
        // We use opacity-0 here so that it doesn't cause the layout to change
        // when this button changes if the instructor adds seeded text
        className={twMerge(className, hidden && "pointer-events-none opacity-0")}
        disabled={hidden}
        kind="tertiary"
        size="s"
        onClick={handleClick}
      >
        <PageIcon />
      </SquareButton>
    </Tooltip>
  );
};
